import React, { useCallback } from "react";
import PropTypes from "prop-types";

import GenericCard from "components/UI/GenericCard/GenericCard";
import AntTable from "components/UI/AntTable";
import dayjs from "dayjs";
import ConfirmPosition from "../Modals/ConfirmPosition";
import { Button, Popconfirm, Space, Tag } from "antd";
import { apiApproveBuyProposal, apiRejectBuyProposal } from "api/apiTrades";

// semi-automation: AutoTrader waits for the user's approval
const isAwaitingApproval = (deal) => deal.approval?.status === "awaiting";

const PendingDeal = (props) => {
  const { pendingDeal, getUserStrategies } = props;

  const sortByNumber = useCallback((a, b) => {
    // handle null / N/A values
    if (a === b) return 0;
    if (a === null || a === "N/A") return -1;
    if (b === null || b === "N/A") return 1;
    return a > b ? 1 : -1;
  }, []);

  const sortByText = useCallback((a, b) => {
    return a.toLowerCase().localeCompare(b.toLowerCase());
  }, []);

  const decide = async (apiCall, dealId) => {
    try {
      await apiCall(dealId);
    } finally {
      await getUserStrategies();
    }
  };

  const columns = [
    {
      title: "Symbol",
      dataIndex: "symbol",
    },
    {
      title: "Adviced Amount",
      dataIndex: "advicedBuyAmount",
      sorter: {
        compare: (a, b) => sortByNumber(a.advicedBuyAmount, b.advicedBuyAmount),
      },
    },
    {
      title: "Adviced Price",
      dataIndex: "advicedBuyPrice",
      sorter: {
        compare: (a, b) => sortByNumber(a.advicedBuyPrice, b.advicedBuyPrice),
      },
    },
    {
      title: "Adviced Value",
      dataIndex: "advicedBuyValue",
      render: (text) => <span>${text}</span>,
      sorter: {
        compare: (a, b) => sortByNumber(a.advicedBuyValue, b.advicedBuyValue),
      },
    },
    {
      title: "Adviced Buy Date",
      dataIndex: "advicedBuyDate",
      render: (text) => <>{dayjs(text).format("DD/MM/YY")}</>,
      sorter: {
        compare: (a, b) => sortByNumber(a.advicedBuyDate, b.advicedBuyDate),
      },
    },
    {
      title: "Time",
      dataIndex: "created",
      render: (text) => <>{dayjs(text).format("HH:mm:ss - DD/MM/YY")}</>,
      sorter: {
        compare: (a, b) => sortByNumber(a.created, b.created),
      },
    },
    {
      title: "Buy",
      key: "uid",
      dataIndex: "",
      render: (text) =>
        isAwaitingApproval(text) ? (
          <Space wrap>
            <Tag color="gold">Awaiting approval</Tag>
            <Popconfirm
              title={`Approve buying ${text.advicedBuyAmount} ${text.symbol}?`}
              description="AutoTrader will place the order."
              okText="Approve"
              onConfirm={() => decide(apiApproveBuyProposal, text._id)}
            >
              <Button type="primary" size="small">
                Approve
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Reject this buy?"
              description="If the buy signal is still active, the next daily run proposes again."
              okText="Reject"
              okButtonProps={{ danger: true }}
              onConfirm={() => decide(apiRejectBuyProposal, text._id)}
            >
              <Button danger size="small">
                Reject
              </Button>
            </Popconfirm>
          </Space>
        ) : (
          <ConfirmPosition
            pendingDeal={text}
            getUserStrategies={getUserStrategies}
          />
        ),
    },
  ];

  return (
    <GenericCard
      title="Pending Deal"
      className="table-card"
      isCardOpenByDefault={true}
      maxHeight={610}
    >
      <AntTable rowKey="_id" dataSource={pendingDeal} columns={columns} />
    </GenericCard>
  );
};
PendingDeal.propTypes = {
  transactions: PropTypes.array,
  getUserStrategies: PropTypes.func,
};

export default PendingDeal;
